import { zustandImmerMiddleware } from '@sketchnote/shared-components';
import { Node } from 'slate';
import { EstimatedTimeObject } from 'src/features/tasks/types/tasks';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { useTimesheetService } from '../api/useTimesheetServices';

type Assignee = {
  firstname: string;
  avatar: string;
  memberId: string;
  lastname: string;
  email: string;
};
export type LogItem = {
  _id: string;
  billable?: boolean;
  startDate?: Date;
  startTime?: Date;
  endTime?: Date;
  member?: string;
  duration?: {
    hours: string;
    minutes: string;
  };
};

export type LogUser = {
  avatar: string;
  email: string;
  firstname: string;
  lastname: string;
  memberId: string;
  estimate?: string;
  logs: LogItem[];
};

export type TaskData = {
  boardId: string;
  name: string;
  key: string;
  number: number;
  total_estimate?: string;
  totalTrackedTime: number;
  watch: boolean;
  assignDate?: string[];
  assignee?: Assignee[];
  logs: LogUser[];
};

export type GlobalTaskData = {
  id: string;
  _id: string;
  assignee: string[];
  board: {
    _id: string;
  };
  boardId: {
    _id: string;
  };
  name: Node[];
  assignDate?: string[];
  start_date?: string[];
  end_date?: string;
  logs: {
    billable: boolean;
    createdAt: string;
    duration: { hours: string; minutes: string; seconds: string };
    endTime: string;
    member: string;
    startDate: string;
    startTime: string;
    task: string;
    tasktype: string;
    updatedAt: string;
  }[];
  estimates: EstimatedTimeObject[];
  workspace: {
    name: string;
    _id: string;
  };
  file: {
    title: string;
    _id: string;
  };
  subtasks: {
    assignee: string[];
    estimates: EstimatedTimeObject[];
    logs: LogItem[];
    name: string;
    _id: string;
  }[];
};

export type TimesheetStoreState = {
  timelogs: TaskData[];
  globalTimelogs: GlobalTaskData[];
  loadingLogs: boolean;
  getTimesheet: (id: string) => void;
  getGlobalTimesheet: () => void;
  deleteTimeLogEntry: (logItemId: string) => void;
  updateTimeLogEntry: ({
    logItemId,
    billable,
    duration: { hours, minutes },
    startDate: startDate,
  }: {
    logItemId: string;
    billable: boolean;
    duration: {
      hours: string;
      minutes: string;
    };
    startDate: Date;
  }) => void;
};

const getInitialValue = () => [
  {
    boardId: '',
    name: '',
    key: '',
    number: 0,
    total_estimate: '',
    totalTrackedTime: 0,
    watch: false,
    assignDate: [''],
    assignee: [
      {
        firstname: '',
        avatar: '',
        memberId: '',
        lastname: '',
        email: '',
      },
    ],
    logs: [
      {
        avatar: '',
        email: '',
        firstname: '',
        lastname: '',
        memberId: '',
        estimate: '',
        logs: [
          {
            _id: '',
            billable: false,
            startDate: undefined,
            startTime: undefined,
            endTime: undefined,
            duration: {
              hours: '0',
              minutes: '0',
            },
          },
        ],
      },
    ],
  },
];

const initialGlobalTimelog = [
  {
    _id: '',
    assignee: [],
    board: {
      _id: '',
    },
    name: [
      {
        type: 'p',
        children: [
          {
            text: '',
          },
        ],
      },
    ],
    estimates: [
      {
        type: 'total' as EstimatedTimeObject['type'],
        member: null,
        estimate: {
          days: 0,
          hours: 0,
          minutes: 0,
          text: '',
        },
        _id: '',
      },
    ],
    logs: [],
    subtasks: [],
    boardId: {
      _id: '',
    },
    id: '',
    assignDate: [],
    workspace: {
      name: '',
      _id: '',
    },
    file: {
      title: '',
      _id: '',
    },
  },
];

export const useTimesheetStore = create<
  TimesheetStoreState,
  [['zustand/devtools', TimesheetStoreState], ['zustand/immer', TimesheetStoreState]]
>(
  devtools(
    zustandImmerMiddleware((set, get) => ({
      timelogs: getInitialValue(),
      globalTimelogs: initialGlobalTimelog,
      loadingLogs: true,
      getTimesheet: async (boardId: string) => {
        const timeSheet = await useTimesheetService().fetchTimesheetDetails({
          id: boardId,
        });
        set((s) => {
          s.timelogs = timeSheet;
        });
      },
      getGlobalTimesheet: async () => {
        const timeSheet = await useTimesheetService().fetchGlobalTimesheetDetails();
        if (timeSheet) {
          set((s) => {
            s.globalTimelogs = timeSheet;
            s.loadingLogs = false;
          });
        }
      },

      deleteTimeLogEntry: async (logItemId: string) => {
        const result = await useTimesheetService().deleteTimeLogEntry(logItemId);
        if (result.success) {
          let flag = false;
          set((s) => {
            for (const task of s.timelogs) {
              for (const logUser of task.logs) {
                for (let i = 0; i < logUser.logs?.length ?? 0; i++) {
                  const logItem = logUser.logs[i];
                  if (logItemId === logItem._id) {
                    logUser.logs = logUser.logs?.filter((logItem) => logItemId !== logItem._id);
                    flag = true;
                    break;
                  }
                }
                if (flag) break;
              }
              if (flag) break;
            }
          });
        }
        return result.success;
      },

      updateTimeLogEntry: async ({
        logItemId,
        billable,
        duration: { hours, minutes },
        startDate: startDate,
      }: {
        logItemId: string;
        billable: boolean;
        duration: { hours: string; minutes: string };
        startDate: Date;
      }) => {
        const result = await useTimesheetService().updateTimeLogEntry({
          logItemId,
          billable,
          duration: {
            hours: hours,
            minutes: minutes,
          },
          startDate,
        });
        if (result?.data.success) {
          let flag = false;
          set((s) => {
            for (const task of s.timelogs) {
              for (const logUser of task.logs) {
                for (let i = 0; i < logUser.logs?.length ?? 0; i++) {
                  const logItem = logUser.logs[i];
                  if (logItemId === logItem._id) {
                    logUser.logs[i] = result?.data.data;
                    flag = true;
                    break;
                  }
                }
                if (flag) break;
              }
              if (flag) break;
            }
          });
        }
      },
    }))
  )
);
